import { useContext, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
} from '@ea-lab/reactive-json';

// Match both the accent variant ("Évolution") returned by the API and the
// original un-accented form ("Evolution") kept for backward compat.
const SPECIAL_TYPES = ['Evolution', 'Évolution', 'Tâche préventive', 'MEP'];

const getBadgeClass = (label) => {
    if (SPECIAL_TYPES.includes(label)) {
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    }
    if (label === 'Sub-task') {
        return 'bg-amber-100 text-amber-800 border border-amber-200';
    }
    return 'bg-sky-100 text-sky-700 border border-sky-200';
};

/**
 * TaskTypeBadge — reactive-json form element that replicates the task-type
 * pill from TaskEdit: a colored badge in display mode that becomes an
 * auto-focused <select> on click.
 *
 * Props:
 *   dataLocation   – path to the stored TID (e.g. ~~.taskSyncable.data.type)
 *   dynamicOptions – array of { value: TID, label: string } from the taxonomy
 *   defaultLabel   – fallback label when no option matches (default: "Tâche")
 */
export const TaskTypeBadge = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const selectRef = useRef(null);

    const { formData: currentTid, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: '',
        globalDataContext,
        templateContext,
    });

    const options = evaluateTemplateValue({
        valueToEvaluate: props.dynamicOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const [isEditing, setIsEditing] = useState(false);

    const currentOption = options.find(o => String(o.value) === String(currentTid));
    const currentLabel = currentOption?.label || (props.defaultLabel ?? 'Tâche');

    const handleBadgeClick = () => {
        setIsEditing(true);
        setTimeout(() => selectRef.current?.focus(), 0);
    };

    const handleChange = (e) => {
        globalDataContext.updateData(e.currentTarget.value, formDataPath);
        setIsEditing(false);
    };

    const handleBlur = () => setIsEditing(false);

    if (isEditing) {
        return (
            <ActionDependant {...props}>
                <select
                    ref={selectRef}
                    value={String(currentTid)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus
                    className="px-3 py-1.5 text-sm font-bold rounded-lg bg-slate-100 border-none ring-2 ring-iris-500 focus:outline-none cursor-pointer"
                >
                    {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </ActionDependant>
        );
    }

    return (
        <ActionDependant {...props}>
            <span
                onClick={handleBadgeClick}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider inline-flex items-center shadow-sm select-none cursor-pointer hover:shadow-md hover:scale-105 transition-all ${getBadgeClass(currentLabel)}`}
                title="Cliquez pour modifier"
            >
                {currentLabel}
            </span>
        </ActionDependant>
    );
};
